/* eslint-disable max-lines */
import { ContentState, RawDraftContentState } from 'draft-js';
import { omit } from 'lodash-es';
import { defineMessages } from 'react-intl';

import {
  COUNTRY_CODES,
  EDUCATION_LEVEL,
  ID_COUNTRY_LOCATION,
  JOB_STATUS,
  JOB_TYPE,
  JobApplicationMethodOption,
  MY_COUNTRY_LOCATION,
  SG_COUNTRY_LOCATION,
  VN_COUNTRY_LOCATION,
  WorkArrangementOption,
} from '../../../common/constants';
import {
  countryCodeIsMalaysia,
  countryCodeIsSingapore,
} from '../../../common/helpers/countryCodeHelper';
import {
  HierarchicalLocation,
  Industry,
  JobBenefit,
  JobKeywords,
  JobScreeningQuestionSelectedQuestions,
  JobSkill,
  KeywordMatchCriteria,
  Language,
  PaymentFrequency,
  Skill,
  WorkAddress,
  WorkShift,
} from '../../../common/interfaces/entities';

export const getJobDescriptionTemplateBasedOnCountry = (
  countryCode: string
) => {
  switch (countryCode.toUpperCase()) {
    case 'ID':
      return 'https://docs.google.com/document/d/12EC32Nq9ZDQ_FjlSLkqMAg6l8R_fBNJJa-4V9Od0d3E';
    default:
      return 'https://goo.gl/wrCqRY';
  }
};

type Nullable<T> = { [K in keyof T]: T[K] | null };

export enum JobStatus {
  DRAFT = 'DRAFT',
  OPEN = 'OPEN',
  CLOSED = 'CLOSED',
  IN_REVIEW = 'IN_REVIEW',
}

export enum Seniority {
  LESS_THAN_A_YEAR = '0-1',
  ONE_TO_THREE = '1-3',
  THREE_TO_FIVE = '3-5',
  FIVE_TO_TEN = '5-10',
  TEN_PLUS = '10-50',
  NO_PREFERENCE = '0-50',
}

export const jobStatusLabels = defineMessages({
  [JobStatus.DRAFT]: {
    id: 'text-draft',
    defaultMessage: 'Draft',
  },
  [JobStatus.OPEN]: {
    id: 'text-active',
    defaultMessage: 'Active',
  },
  [JobStatus.CLOSED]: {
    id: 'interactive-inactive',
    defaultMessage: 'Inactive',
  },
  [JobStatus.IN_REVIEW]: {
    id: 'text-in-review',
    defaultMessage: 'In Review',
  },
  DELETED: {
    id: 'text-deleted',
    defaultMessage: 'Deleted',
  },
});

export const GenderPreferenceForJob = {
  MALE: 'MALE',
  FEMALE: 'FEMALE',
  NO_PREFERENCE: 'NO_PREFERENCE',
} as const;

export type GenderPreferenceForJob =
  (typeof GenderPreferenceForJob)[keyof typeof GenderPreferenceForJob];

export const footerMessages = defineMessages({
  saveChanges: {
    id: 'text-changes-need-saved',
    defaultMessage: 'Changes need to be saved',
  },
});

export const subscriptionMessages = defineMessages({
  hardLimitReachedTitle: {
    id: 'text-can-hire-count-roles',
    defaultMessage:
      'You can only hire {count, plural, =1 {1 role at a time} other {{count} roles at the same time}}',
  },
  upgradeSubscriptionPostJobDescription: {
    id: 'text-upgrade-subscription-to-post',
    defaultMessage:
      'Upgrade your subscription plan to post this job and hire more than {count, plural, =1 {1 role} other {{count} roles}} at the same time.',
  },
  upgradeSubscriptionReactivateJobDescription: {
    id: 'text-upgrade-subscription-to-reactivate',
    defaultMessage:
      'Upgrade your subscription plan to reactivate this job and hire more than {count, plural, =1 {1 role} other {{count} roles}} at the same time.',
  },
  deactivateJobInfo: {
    id: 'text-deactivate-if-made-hiring',
    defaultMessage:
      'You can also deactivate any of your active jobs if you have made a hiring.',
  },
  saveJobDraftInfo: {
    id: 'text-now-job-saved-draft',
    defaultMessage: 'For now, this job is saved as <bold>a draft</bold>.',
  },
  upgradeNow: {
    id: 'interactive-upgrade-now',
    defaultMessage: 'Upgrade Now',
  },
  manageActiveJobs: {
    id: 'interactive-manage-active-jobs',
    defaultMessage: 'Manage Active Jobs',
  },
  interactiveCancel: {
    id: 'interactive-cancel',
    defaultMessage: 'Cancel',
  },
});

export const CustomQuestionPlainTexts = {
  CUSTOM_PLAIN_TEXT_A: 'CUSTOM_PLAIN_TEXT_A',
  CUSTOM_PLAIN_TEXT_B: 'CUSTOM_PLAIN_TEXT_B',
  CUSTOM_PLAIN_TEXT_C: 'CUSTOM_PLAIN_TEXT_C',
} as const;

export type CustomQuestionPlainTexts =
  (typeof CustomQuestionPlainTexts)[keyof typeof CustomQuestionPlainTexts];

export const SelectedQuestionsFormState = {
  ...omit(JobScreeningQuestionSelectedQuestions, 'CUSTOM_PLAIN_TEXT'),
  ...CustomQuestionPlainTexts,
};

export type SelectedQuestionsFormState =
  (typeof SelectedQuestionsFormState)[keyof typeof SelectedQuestionsFormState];

export type ScreeningQuestionFormState = {
  selectedQuestions?: SelectedQuestionsFormState[];
  introVideoPreferredLanguage?: string;
  documentAndCertificates?: {
    id: string;
    name: string;
  }[];
  industries?: Industry[];
  languages?: Language[];
  customQuestionPlainTextA?: string;
  customQuestionPlainTextB?: string;
  customQuestionPlainTextC?: string;
};

export const customQuestionPlainTextToFieldName: Record<
  CustomQuestionPlainTexts,
  | 'customQuestionPlainTextA'
  | 'customQuestionPlainTextB'
  | 'customQuestionPlainTextC'
> = {
  [CustomQuestionPlainTexts.CUSTOM_PLAIN_TEXT_A]: 'customQuestionPlainTextA',
  [CustomQuestionPlainTexts.CUSTOM_PLAIN_TEXT_B]: 'customQuestionPlainTextB',
  [CustomQuestionPlainTexts.CUSTOM_PLAIN_TEXT_C]: 'customQuestionPlainTextC',
};

export const MAX_LANGUAGE_SELECTED = 3;
export const MAX_CUSTOM_PLAIN_TEXT_LENGTH = 200;

export interface JobFormState {
  title: string;
  type: (typeof JOB_TYPE)[keyof typeof JOB_TYPE];
  JobCategoryId?: number;
  categoryName: string;
  workArrangementOption: WorkArrangementOption;
  CityId: number | null;
  CountryCode: string;
  countryName: string;
  acceptsForeignApplications: boolean;
  description: ContentState;
  descriptionRaw: RawDraftContentState;
  invalidDescriptionError?: string;
  seniority?: Seniority;
  skills: JobSkill[];
  addBonus: boolean;
  salaryMode: BonusFrequency;
  salaryRate: BonusFrequency | null;
  minSalaryAmount: number | null;
  maxSalaryAmount: number | null;
  minBonusAmount: number;
  maxBonusAmount: number;
  shouldShowSalary: boolean;
  CurrencyCode: string;
  jobDraftId?: string;
  isEdit?: boolean;
  isCopy?: boolean;
  initializeRememberedCompanyAddress?: boolean;
  interviewProcess?: ContentState;
  interviewProcessRaw?: RawDraftContentState | null;
  shouldPublishOnLinkedin?: boolean;
  benefits?: JobBenefit[];
  receiverIds: string[];
  HierarchicalJobCategoryId?: string;
  HierarchicalJobCategoryName?: string;
  provinceLocation: HierarchicalLocation | null;
  cityLocation: HierarchicalLocation | null;
  districtLocation: HierarchicalLocation | null;
  districtOptions?: HierarchicalLocation[];
  companyAddress?: WorkAddress | null;
  educationLevel: EDUCATION_LEVEL;
  originalHierarchicalJobCategoryId?: string | null;
  originalLocationId?: string | null;
  originalDistrictId?: string | null;
  historyJobId?: string | null;
  CreatorId?: string;
  originalCreatorId?: string | null;
  isSuggestedSalaryApplied?: boolean;
  isSuggestedRequirementsApplied?: boolean;
  userChangedSalary?: boolean;
  userChangedRequirements?: boolean;
  userChangedJobType?: boolean;
  jobTypeSuggestionTitle?: string | null;
  isJobDescriptionAutoGenerated?: boolean;
  isWhitelistedCountry?: boolean;
  minAge: number | null;
  maxAge: number | null;
  noAgeLimit: boolean;
  gender: GenderPreferenceForJob;
  unfilteredSkills: Skill[];
  haveUnfilteredSkills: boolean;
  isLocationRequiredForCandidate?: boolean | null;
  isEducationRequiredForCandidate?: boolean | null;
  isResumeRequiredForCandidate?: boolean | null;
  step: JobPostingPageStep;
  isGeneratingJobDescription?: boolean;
  screeningQuestions?: ScreeningQuestionFormState | null;
  screeningQuestionsEnabled?: boolean;
  isProfilePicRequiredForCandidate?: boolean | null;
  jobApplicationMethodOption: JobApplicationMethodOption;
  externalApplyURL?: string | null;
  provinceMinimumWage?: number | null;
  workShifts?: WorkShift[] | null;
  workingDaysPerWeek?: number | null;
  requiresWeekendHolidays?: boolean | null;
  paymentFrequency?: PaymentFrequency | null;
  showWorkDetails?: boolean | null;
  showPaymentFrequency?: boolean | null;
  shouldShowMatchCriteria?: boolean;
  keywordMatchCriteria?: KeywordMatchCriteria;
}

export enum SalaryType {
  BASIC = 'BASIC',
  BONUS = 'BONUS',
}

export interface SalaryBasic {
  salaryType: SalaryType.BASIC;
  salaryMode: BonusFrequency.MONTH;
  minAmount: number;
  maxAmount: number | null;
  CurrencyCode: string;
  paymentFrequency?: string;
}

export interface SalaryBonus {
  salaryType: SalaryType.BONUS;
  salaryMode: BonusFrequency;
  minAmount: number;
  maxAmount: number;
  CurrencyCode: string;
}

type ScreeningQuestionPayload = {
  selectedQuestions: JobScreeningQuestionSelectedQuestions[];
  introVideoPreferredLanguage?: string;
  documentAndCertificateIds?: string[];
  industryIds?: number[];
  customQuestionPlainTexts?: string[];
  languageCodes?: string[];
};

export interface JobPostingPayload {
  status: JobStatus;
  CompanyId: string;
  numberOfHires: number;
  receiverIds: string[];
  title: string;
  type: (typeof JOB_TYPE)[keyof typeof JOB_TYPE];
  JobCategoryId: number;
  workArrangementOption: WorkArrangementOption;
  CityId: number;
  CountryCode: string;
  acceptsForeignApplications: boolean;
  descriptionRaw: RawDraftContentState;
  salaries: [SalaryBasic, SalaryBonus?];
  skills: { id: string; mustHave: boolean }[];
  shouldShowSalary: boolean;
  minYearsOfExperience?: number;
  maxYearsOfExperience?: number;
  interviewProcessRaw?: RawDraftContentState | null;
  shouldPublishOnLinkedin?: boolean;
  benefits?: Pick<JobBenefit, 'benefit' | 'description'>[];
  shouldShowBenefits?: boolean;
  educationLevel: EDUCATION_LEVEL;
  HierarchicalJobCategoryId: string;
  CreatorId?: string;
  minAge: number | null;
  maxAge: number | null;
  gender: Omit<GenderPreferenceForJob, 'NO_PREFERENCE'> | null;
  jobDraftId?: string;
  screeningQuestions?: ScreeningQuestionPayload;
  externalApplyURL?: string | null;
  CompanyAddressId?: string | null;
  keywordMatchCriteria?: { questionGroupId: string; keywords: JobKeywords[] };
}

export type JobDraftFormState = Partial<Nullable<JobFormState>>;
export type JobDraftPostingPayload = Partial<Nullable<JobPostingPayload>>;

export const JOB_POSTING_ID = 'jobv2-job-posting';
export const JOB_EDITING_ID = 'jobv2-job-editing';

export const DEFAULT_NUMBER_OF_HIRES = 1;

export const DEFAULT_JOB_STATUS = JOB_STATUS.OPEN;

export enum BonusFrequency {
  HOUR = 'HOUR',
  DAY = 'DAY',
  WEEK = 'WEEK',
  MONTH = 'MONTH',
  YEAR = 'YEAR',
  PROJECT = 'PROJECT',
}

export enum SalaryPaymentType {
  RANGE = 'RANGE',
  BASE = 'BASE',
}

export const JobEditTabs = {
  DETAILS: 'DETAILS',
  JOB_DESCRIPTION: 'JOB_DESCRIPTION',
  SCREENING_QUESTIONS: 'SCREENING_QUESTIONS',
  ENHANCE: 'ENHANCE',
} as const;

export type JobEditTabs = (typeof JobEditTabs)[keyof typeof JobEditTabs];

type CountryInfo = {
  id: string;
  name: string;
  code: string;
  currency: string;
};

export const INDONESIA_COUNTRY = { ...ID_COUNTRY_LOCATION, currency: 'IDR' };
export const VIETNAM_COUNTRY = { ...VN_COUNTRY_LOCATION, currency: 'VND' };
export const SINGAPORE_COUNTRY = { ...SG_COUNTRY_LOCATION, currency: 'SGD' };
export const MALAYSIA_COUNTRY = { ...MY_COUNTRY_LOCATION, currency: 'MYR' };

const COUNTRY_DATA: Record<string, CountryInfo> = {
  [COUNTRY_CODES.ID]: INDONESIA_COUNTRY,
  [COUNTRY_CODES.VN]: VIETNAM_COUNTRY,
  [COUNTRY_CODES.SG]: SINGAPORE_COUNTRY,
  [COUNTRY_CODES.MY]: MALAYSIA_COUNTRY,
};

export const getDefaultCountry = (countryCode: string) =>
  COUNTRY_DATA[countryCode] ?? INDONESIA_COUNTRY;

type OverridenValues = Record<string, any>;

export const makeRequiredFormDefaultValues = (
  countryCode: string,
  overridenValues: OverridenValues
): Partial<JobFormState> => {
  const defaultCountry = getDefaultCountry(countryCode);

  const defaultValues: Partial<JobFormState> = {
    type: JOB_TYPE.FULL_TIME,
    workArrangementOption: WorkArrangementOption.ONSITE,
    CountryCode: defaultCountry.code,
    countryName: defaultCountry.name,
    shouldShowSalary: true,
    CurrencyCode: defaultCountry.currency,
    addBonus: false,
    salaryMode: BonusFrequency.MONTH,
    salaryRate: null,
    paymentFrequency: PaymentFrequency.MONTHLY,
    isEdit: false,
    isCopy: false,
    userChangedJobType: false,
    jobTypeSuggestionTitle: null,
    skills: [],
    minSalaryAmount: null,
    maxSalaryAmount: null,
    title: '',
    provinceLocation: null,
    cityLocation: null,
    districtLocation: null,
    companyAddress: null,
    isWhitelistedCountry: true,
    noAgeLimit: true,
    minAge: null,
    maxAge: null,
    gender: GenderPreferenceForJob.NO_PREFERENCE,
    unfilteredSkills: [],
    haveUnfilteredSkills: false,
    shouldPublishOnLinkedin: countryCode === VIETNAM_COUNTRY.code,
    step: JobPostingPageStep.BASIC_INFO,
    jobApplicationMethodOption: JobApplicationMethodOption.GLINTS,
    shouldShowMatchCriteria: false,
    keywordMatchCriteria: { questions: [], answers: [] },
  };

  return {
    ...defaultValues,
    ...overridenValues,
    // A new draft can remount before its first autosave has populated these
    // fields. Preserve only their company-derived defaults in that window.
    CountryCode: overridenValues.CountryCode ?? defaultCountry.code,
    countryName: overridenValues.countryName ?? defaultCountry.name,
    CurrencyCode: overridenValues.CurrencyCode ?? defaultCountry.currency,
  };
};

export interface DraftURLParams {
  draftId: string;
}

export type CompanyMembersOptions = {
  value: string;
  label: string;
  displayName: string;
  email: string;
  isDeletable: boolean;
  userId: string;
  profilePic: string | null;
  role?: string;
};

export enum REOPEN_JOB_PAGE {
  DASHBOARD = 'DASHBOARD',
  MANAGE_CANDIDATES = 'MANAGE_CANDIDATES',
}

export const CLOSE_JOB_PAGE = {
  DASHBOARD: 'DASHBOARD',
  JOB_EDIT_PAGE: 'JOB_EDIT_PAGE',
  MANAGE_CANDIDATES: 'MANAGE_CANDIDATES',
} as const;

export type CLOSE_JOB_PAGE =
  (typeof CLOSE_JOB_PAGE)[keyof typeof CLOSE_JOB_PAGE];

export const JobPostingSeniority = {
  LESS_THAN_A_YEAR: '0-1',
  ONE_TO_THREE: '1-3',
  THREE_TO_FIVE: '3-5',
  FIVE_TO_TEN: '5-10',
  TEN_PLUS: '10-50',
  NO_PREFERENCE: '0-50',
};

export type CountrySelect = {
  value: string;
  label: string;
  currencyCode: string;
};

export const getMinAgeForCountry = (countryCode: string) => {
  if (countryCodeIsMalaysia(countryCode)) {
    return 15;
  }
  if (countryCodeIsSingapore(countryCode)) {
    return 16;
  }
  return 18;
};
export const MAX_AGE = 65;

export const JobPostingPageStep = {
  BASIC_INFO: 1,
  REQUIREMENTS: 2,
  JOB_DESCRIPTION: 3,
  SCREENING_QUESTIONS: 4,
} as const;

export const OrderedJobPostingPageStep = [
  JobPostingPageStep.BASIC_INFO,
  JobPostingPageStep.REQUIREMENTS,
  JobPostingPageStep.JOB_DESCRIPTION,
  JobPostingPageStep.SCREENING_QUESTIONS,
] as const;

export type JobPostingPageStep =
  (typeof JobPostingPageStep)[keyof typeof JobPostingPageStep];

export const WORK_SHIFT_LABELS = defineMessages({
  [WorkShift.MORNING]: {
    id: 'text-morning-shift',
    defaultMessage: 'Morning Shift',
  },
  [WorkShift.AFTERNOON]: {
    id: 'text-afternoon-shift',
    defaultMessage: 'Afternoon Shift',
  },
  [WorkShift.NIGHT]: {
    id: 'text-night-shift',
    defaultMessage: 'Night Shift',
  },
  [WorkShift.REGULAR]: {
    id: 'text-regular-working-hours',
    defaultMessage: 'Regular working hours',
  },
});

export const PAYMENT_FREQUENCY_LABELS = defineMessages({
  [PaymentFrequency.DAILY]: {
    id: 'interactive-daily',
    defaultMessage: 'Daily',
  },
  [PaymentFrequency.WEEKLY]: {
    id: 'interactive-weekly',
    defaultMessage: 'Weekly',
  },
  [PaymentFrequency.MONTHLY]: {
    id: 'interactive-monthly',
    defaultMessage: 'Monthly',
  },
});

export const SALARY_RATE_LABELS = defineMessages({
  [BonusFrequency.HOUR]: {
    id: 'interactive-per-hour',
    defaultMessage: 'Per hour',
  },
  [BonusFrequency.DAY]: {
    id: 'interactive-per-day',
    defaultMessage: 'Per day',
  },
  [BonusFrequency.MONTH]: {
    id: 'interactive-per-month',
    defaultMessage: 'Per month',
  },
});

export const SALARY_RATE_VIEW_LABELS = defineMessages({
  [BonusFrequency.HOUR]: {
    id: 'text-frequency-hour',
    defaultMessage: '/hour',
  },
  [BonusFrequency.DAY]: {
    id: 'text-frequency-day',
    defaultMessage: '/day',
  },
  [BonusFrequency.MONTH]: {
    id: 'text-frequency-month',
    defaultMessage: '/month',
  },
});

export const WORKING_DAYS_OPTIONS = [1, 2, 3, 4, 5, 6, 7];

export const PAYMENT_FREQUENCY_TO_SALARY_RATES: Record<
  PaymentFrequency,
  BonusFrequency[]
> = {
  [PaymentFrequency.DAILY]: [BonusFrequency.HOUR, BonusFrequency.DAY],
  [PaymentFrequency.WEEKLY]: [
    BonusFrequency.HOUR,
    BonusFrequency.DAY,
    BonusFrequency.MONTH,
  ],
  [PaymentFrequency.MONTHLY]: [BonusFrequency.DAY, BonusFrequency.MONTH],
};

const ALL_PAYMENT_FREQUENCIES = Object.values(PaymentFrequency);

export const JOB_TYPE_TO_PAYMENT_FREQUENCIES: Record<
  JOB_TYPE,
  PaymentFrequency[]
> = {
  [JOB_TYPE.FULL_TIME]: [PaymentFrequency.WEEKLY, PaymentFrequency.MONTHLY],
  [JOB_TYPE.PART_TIME]: ALL_PAYMENT_FREQUENCIES,
  [JOB_TYPE.CONTRACT]: ALL_PAYMENT_FREQUENCIES,
  [JOB_TYPE.INTERNSHIP]: ALL_PAYMENT_FREQUENCIES,
  [JOB_TYPE.PROJECT_BASED]: ALL_PAYMENT_FREQUENCIES,
  [JOB_TYPE.DAILY]: ALL_PAYMENT_FREQUENCIES,
};

export const JOB_TYPE_TO_SALARY_RATES: Partial<
  Record<JOB_TYPE, Record<PaymentFrequency, BonusFrequency[]>>
> = {
  [JOB_TYPE.FULL_TIME]: {
    [PaymentFrequency.DAILY]: [BonusFrequency.MONTH],
    [PaymentFrequency.WEEKLY]: [BonusFrequency.MONTH],
    [PaymentFrequency.MONTHLY]: [BonusFrequency.MONTH],
  },
};
